/**
 * Human Resources (WALLIYO-0131): the console screen the whole company uses,
 * not only the people who run it.
 *
 * Four tabs. Directory and Learning are open to anybody who can sign in:
 * looking somebody up, and your own training. Recruiting and Employees are the
 * HR function and need MANAGE_HUMAN_RESOURCES — one feature, so there is one
 * tab to close rather than seven.
 *
 * The directory is deliberately its own endpoint rather than a filter on
 * `GET /console/staff`. That one answers with sessions, statuses and the roles
 * behind somebody's access, and needs a feature to read. This one answers with
 * org structure and nothing else: names, teams, who leads them. Open to
 * everybody and carrying what anybody holds, it would be a list of who can do
 * what, readable by anybody — so the two never share a response type, and the
 * shapes here have no field that could carry one.
 */

import type { StaffActionId, StaffFeatureId } from "../actions/types";
import type { MoneyLimit } from "../permissions/index";

/**
 * One feature for the whole HR half: one tab to close, and the seven sub-tabs
 * go with it. Directory and Learning need nothing — looking somebody up, and
 * your own training.
 *
 * Deliberately not what it takes to ask for leave or to decide somebody's.
 * Everybody has leave, and it is decided by whoever leads their team, which is
 * a fact on the membership rather than a feature anybody grants.
 */
export const HR_SCREEN_FEATURES = [
  "MANAGE_HUMAN_RESOURCES_STAFF_FEATURE_ENABLEMENT",
] as const satisfies readonly StaffFeatureId[];

/**
 * Somebody who is here. Deactivated people are not in the directory: it
 * answers "who do I ask about this", and the answer is never somebody who has
 * left.
 */
export type DirectoryStatus = "invited" | "active";

/** A team they are on, and whether they lead it. Closed teams are left out. */
export type DirectoryTeam = {
  staffTeamId: string;
  name: string;
  leads: boolean;
};

/** Somebody named from another person's entry — a lead, or a teammate. */
export type DirectoryPersonRef = {
  uuid: number;
  staffMemberId: string;
  name: string;
  /** The team this reference came from, so "manager" is never a bare name. */
  teamName: string;
};

/**
 * Org structure and how to reach somebody. No features, no permissions, no
 * sessions, no last-seen: those live on the Employees tab, which is gated.
 */
export type DirectoryPerson = {
  uuid: number;
  staffMemberId: string;
  name: string;
  /** Their work address. The directory exists so people can be reached. */
  email: string;
  /** Role names as they read on the screen, never feature or setting ids. */
  roles: readonly string[];
  teams: readonly DirectoryTeam[];
  /**
   * Whoever leads the teams they are on. Not a stored field — somebody on two
   * teams has two managers, which is the honest answer when they do two
   * different jobs, and somebody whose team has no lead has none.
   */
  managers: readonly DirectoryPersonRef[];
  status: DirectoryStatus;
};

/**
 * GET /api/v1/console/directory?q=
 *
 * Open to anybody signed in. A plain read: not audited. `q` matches a name or
 * an exact UUID; empty lists everybody, by name.
 */
export type ListDirectoryResponse = {
  people: readonly DirectoryPerson[];
  /** How many matched, when more matched than were returned. */
  total: number;
};

/**
 * GET /api/v1/console/directory/:uuid
 *
 * Open to anybody signed in. A plain read: not audited. Everybody on their
 * teams comes with them, because "who else is on this" is the question that
 * follows looking somebody up.
 */
export type GetDirectoryPersonResponse = {
  person: DirectoryPerson;
  /** Everybody who shares a team with them, each named with the team. */
  teammates: readonly DirectoryPersonRef[];
};

// ── Leaving ──────────────────────────────────────────────────────────────
// Deactivating was one button. Somebody leaving has loose ends it does not
// touch, and two of them rot quietly because nothing ever complains about
// them: a team left without a lead, and a grant made to them by name with no
// end date. So they are steps rather than something to remember.

/** A team they lead, and what happens to it when they go. */
export type LeavingTeam = {
  staffTeamId: string;
  name: string;
  /** How many other people are on it. */
  otherMembers: number;
  /**
   * True when nobody else leads it. Then nobody approves its leave, reviews
   * anybody on it, or attests to their access — which are the same fact.
   */
  wouldHaveNoLead: boolean;
};

/** Something granted to them by name rather than through a role. */
export type LeavingGrant = {
  settingId: string;
  /** Null is the one that matters: it never takes itself away. */
  expiresAt: string | null;
  reason: string | null;
};

/**
 * GET /api/v1/console/staff/:staffMemberId/leaving
 *
 * Needs any one of HR_SCREEN_FEATURES. A plain read: not audited. Every write
 * it leads to — handing a team over, revoking a grant, deactivating — is its
 * own audited action.
 */
export type GetLeavingResponse = {
  staffMemberId: string;
  uuid: number;
  name: string;
  /** Already gone: the screen says so instead of offering the steps again. */
  alreadyLeft: boolean;
  teamsTheyLead: readonly LeavingTeam[];
  /** Granted to them by name. Ordered with the never-expiring ones first. */
  directGrants: readonly LeavingGrant[];
  /** Open console sessions. Deactivating does end these. */
  openSessions: number;
};

// ── Time off ─────────────────────────────────────────────────────────────
// Everybody has leave, so asking needs no feature. It is decided by whoever
// leads your team — the `leads` flag on the membership, read from the other
// end — which is the same fact that decides who reviews you and who attests
// to your access. One lookup, three screens.

export type TimeOffKind = "annual" | "sick" | "unpaid" | "parental" | "other";
export type TimeOffStatus = "pending" | "approved" | "refused" | "withdrawn";

export type TimeOffRequest = {
  timeOffRequestId: string;
  staffMemberId: string;
  uuid: number;
  name: string;
  kind: TimeOffKind;
  startsOn: string;
  endsOn: string;
  /**
   * Counted when the request was made, never derived on read: working days
   * depend on a calendar that changes, and a request already decided should
   * not quietly become a different number of days afterwards.
   */
  days: number;
  reason: string | null;
  status: TimeOffStatus;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionNote: string | null;
  version: number;
  createdAt: string;
};

/** What somebody has left this year, and what it was worked out from. */
export type TimeOffBalance = {
  staffMemberId: string;
  name: string;
  /** Null when nobody has set an allowance on their employment record. */
  allowanceDays: number | null;
  /** Approved annual leave in the current year. */
  takenDays: number;
  /** Asked for and not yet decided. Not taken, but not free either. */
  pendingDays: number;
  /** Null when there is no allowance to subtract from. */
  leftDays: number | null;
};

/**
 * GET /api/v1/console/time-off
 *
 * No feature. What comes back depends on who is asking: your own requests
 * always, plus everything from the teams you lead, plus everything at all for
 * somebody in HR. A plain read: not audited.
 */
export type ListTimeOffResponse = {
  /** Yours, whoever you are. */
  mine: readonly TimeOffRequest[];
  /** Waiting on you, because you lead the team of whoever asked. */
  toDecide: readonly TimeOffRequest[];
  /** Everybody's, for somebody in HR. Empty otherwise. */
  everybody: readonly TimeOffRequest[];
  /** Away today, so a lead can see the shape of the week. */
  awayToday: readonly TimeOffRequest[];
  balance: TimeOffBalance;
};

/** POST /api/v1/console/time-off — asking. Audited as REQUEST_TIME_OFF. */
export type RequestTimeOffRequest = {
  kind: TimeOffKind;
  startsOn: string;
  endsOn: string;
  days: number;
  reason?: string | null;
};

export type RequestTimeOffResponse = { request: TimeOffRequest };

/**
 * POST /api/v1/console/time-off/:timeOffRequestId/decide
 *
 * Only whoever leads their team, and never your own. Audited as
 * DECIDE_TIME_OFF, with the person it was about named on the entry.
 */
export type DecideTimeOffRequest = {
  approve: boolean;
  reason: string;
  version: number;
};

export type DecideTimeOffResponse = { request: TimeOffRequest };

// ── Reviews ──────────────────────────────────────────────────────────────
// The other end of a grant. A grant with an end date takes itself away; one
// without never does, and nothing else in the console ever complains about it.
// So this tab is the complaint: what is held by name with no expiry, what is
// held by somebody who has left, and which leads owe their people a look.

/** Why a grant is worth a look. Said, rather than left to be worked out. */
export type GrantConcern = "never_ends" | "holder_left" | "never_used";

export type GrantToReview = {
  staffMemberId: string;
  uuid: number;
  name: string;
  /** False when they have left and the grant is still live. */
  stillHere: boolean;
  settingId: string;
  reason: string | null;
  /**
   * When the grant last moved, which is what the row records. Not when it was
   * first made: a value row is written over on change, and the moment it was
   * granted lives in the audit log, which is the thing that keeps history.
   */
  changedAt: string;
  expiresAt: string | null;
  concern: GrantConcern;
};

/** A lead, and how many of their people hold something granted by name. */
export type LeadOwingReview = {
  staffMemberId: string;
  uuid: number;
  name: string;
  teamName: string;
  /** People on their team holding a grant by name. */
  people: number;
  /** Grants among them that never expire. */
  neverEnding: number;
};

/**
 * GET /api/v1/console/access-reviews
 *
 * Needs any one of HR_SCREEN_FEATURES. A plain read: not audited. Revoking is
 * done on Permissions, where it already is.
 */
export type ListAccessReviewsResponse = {
  /** Worst first: somebody who has left, then a grant that never ends. */
  grants: readonly GrantToReview[];
  leads: readonly LeadOwingReview[];
};

// ── Payroll ──────────────────────────────────────────────────────────────
// Every figure is an amount with a currency beside it. Neither means anything
// alone, and a column of bare numbers is a column nobody can add up. A run
// copies each gross when the line is made rather than reading through to the
// employment record: a raise next month must not change what was paid last
// month.

export type PayrollLineStatus = "ready" | "held" | "paid";

/** An amount of money. Never a bare number, whatever the currency. */
export type Money = {
  amount: number;
  currency: string;
};

export type PayrollLine = {
  payrollLineId: string;
  staffMemberId: string;
  uuid: number;
  name: string;
  gross: Money;
  /**
   * What was withheld, as an amount rather than a rate. A rate belongs to a
   * jurisdiction; this line belongs to a person, and two people on one run can
   * be under different ones.
   */
  contributions: Money;
  net: Money;
  status: PayrollLineStatus;
  heldReason: string | null;
  version: number;
};

export type PayrollRun = {
  payrollRunId: string;
  /** The first day of the month it pays, as YYYY-MM-DD. */
  periodMonth: string;
  status: "draft" | "closed";
  closedAt: string | null;
  closedByName: string | null;
  version: number;
};

/**
 * A run's totals, one entry per currency.
 *
 * A list rather than a figure, because a company that hires in two currencies
 * has two totals and adding them would produce a number that is not money.
 */
export type PayrollTotals = {
  currency: string;
  gross: number;
  contributions: number;
  net: number;
  people: number;
};

/**
 * GET /api/v1/console/payroll[?month=YYYY-MM]
 *
 * Needs any one of HR_SCREEN_FEATURES. A plain read: not audited.
 */
export type GetPayrollResponse = {
  /** Null before anybody has opened this month. */
  run: PayrollRun | null;
  lines: readonly PayrollLine[];
  totals: readonly PayrollTotals[];
  /** The months that exist, most recent first, for the picker. */
  months: readonly string[];
  /**
   * People with no gross on their employment record. They cannot be paid by a
   * run that does not know what they earn, so they are named rather than
   * quietly left out.
   */
  missingPay: readonly { staffMemberId: string; uuid: number; name: string }[];
};

/**
 * POST /api/v1/console/payroll — opens a month and draws its lines from the
 * employment records as they stand today. Audited as OPEN_PAYROLL.
 */
export type OpenPayrollRequest = {
  /** YYYY-MM. */
  month: string;
  reason: string;
};

export type OpenPayrollResponse = { run: PayrollRun; lines: readonly PayrollLine[] };

/**
 * POST /api/v1/console/payroll/lines/:payrollLineId — holds a line or lets it
 * go. Audited as EDIT_PAYROLL_LINE, naming the person it was about.
 */
export type EditPayrollLineRequest = {
  hold: boolean;
  reason: string;
  version: number;
};

export type EditPayrollLineResponse = { line: PayrollLine };

/**
 * POST /api/v1/console/payroll/:payrollRunId/close
 *
 * A closed month is what was paid. Audited as CLOSE_PAYROLL.
 */
export type ClosePayrollRequest = { reason: string; version: number };

export type ClosePayrollResponse = { run: PayrollRun };

// ── Performance ──────────────────────────────────────────────────────────
// Who reviews you is whoever led your team when the cycle opened. Recorded
// then, not recomputed on read: a team can change lead halfway through, and
// the review still belongs to whoever was asked to write it.

export type ReviewStatus = "not_started" | "in_progress" | "submitted" | "signed_off";

/** Why a review has nobody to write it. Said, rather than left as a blank. */
export type ReviewerGap = "no_team" | "no_lead" | "leads_own_team";

export type PerformanceReview = {
  performanceReviewId: string;
  staffMemberId: string;
  uuid: number;
  name: string;
  /** Null when nobody could be found, and then `gap` says why. */
  reviewerId: string | null;
  reviewerName: string | null;
  gap: ReviewerGap | null;
  status: ReviewStatus;
  outcome: string | null;
  note: string | null;
  submittedAt: string | null;
  version: number;
};

export type ReviewCycle = {
  reviewCycleId: string;
  name: string;
  opensOn: string;
  closesOn: string;
  status: "open" | "closed";
  version: number;
};

/**
 * GET /api/v1/console/review-cycles[?cycle=<id>]
 *
 * No feature to read your own or the ones you were asked to write. The whole
 * cycle needs HR_SCREEN_FEATURES. A plain read: not audited.
 */
export type GetReviewCycleResponse = {
  cycle: ReviewCycle | null;
  cycles: readonly ReviewCycle[];
  /** Reviews this person has to write. */
  toWrite: readonly PerformanceReview[];
  /** This person's own, however far along it is. */
  mine: readonly PerformanceReview[];
  /** Everybody's, for somebody in HR. Empty otherwise. */
  everybody: readonly PerformanceReview[];
};

/**
 * POST /api/v1/console/review-cycles — opens a cycle and makes a review for
 * everybody, each pointed at whoever leads their team today. Audited as
 * OPEN_REVIEW_CYCLE.
 */
export type OpenReviewCycleRequest = {
  name: string;
  opensOn: string;
  closesOn: string;
  reason: string;
};

export type OpenReviewCycleResponse = { cycle: ReviewCycle; reviews: readonly PerformanceReview[] };

/**
 * POST /api/v1/console/reviews/:performanceReviewId
 *
 * Only the reviewer the cycle named, and never your own. Audited as
 * WRITE_REVIEW, naming the person it was about.
 */
export type WriteReviewRequest = {
  outcome: string;
  note: string;
  /** True submits it; false saves it as still being written. */
  submit: boolean;
  version: number;
};

export type WriteReviewResponse = { review: PerformanceReview };

// ── Recruiting ───────────────────────────────────────────────────────────
// A candidate is not a user and not a staff member. They have no UUID here
// until they are hired, which is the moment they become one of us — and that
// is the one stage change that also produces an invitation.

export type JobStatus = "open" | "on_hold" | "filled" | "closed";
export type CandidateStage = "applied" | "screening" | "interview" | "offer" | "hired" | "declined" | "withdrawn";

export type JobCandidate = {
  jobCandidateId: string;
  jobOpeningId: string;
  name: string;
  phone: string | null;
  email: string | null;
  stage: CandidateStage;
  note: string | null;
  /** Set only once they are hired. Null before that. */
  staffMemberId: string | null;
  version: number;
  createdAt: string;
};

export type JobOpening = {
  jobOpeningId: string;
  title: string;
  /** The team that owns the work, and so owns the vacancy. */
  staffTeamId: string | null;
  teamName: string | null;
  description: string | null;
  status: JobStatus;
  openedAt: string;
  closedAt: string | null;
  /** How many candidates are on it, by stage they have not left. */
  liveCandidates: number;
  version: number;
};

/**
 * GET /api/v1/console/job-openings
 *
 * Needs any one of HR_SCREEN_FEATURES: a pipeline carries candidates' names
 * and phone numbers, which is not org structure. A plain read: not audited.
 */
export type ListJobOpeningsResponse = {
  openings: readonly JobOpening[];
  candidates: readonly JobCandidate[];
};

/** POST /api/v1/console/job-openings. Audited as OPEN_JOB. */
export type OpenJobRequest = {
  title: string;
  staffTeamId?: string | null;
  description?: string | null;
  reason: string;
};

export type OpenJobResponse = { opening: JobOpening };

/** PATCH /api/v1/console/job-openings/:jobOpeningId. Audited as EDIT_JOB. */
export type EditJobRequest = {
  title?: string;
  staffTeamId?: string | null;
  description?: string | null;
  status?: JobStatus;
  reason: string;
  version: number;
};

export type EditJobResponse = { opening: JobOpening };

/**
 * POST /api/v1/console/job-openings/:jobOpeningId/candidates — adds somebody,
 * or moves one already there. Audited as MOVE_CANDIDATE.
 */
export type AddCandidateRequest = {
  name: string;
  phone?: string | null;
  email?: string | null;
  note?: string | null;
  reason: string;
};

export type AddCandidateResponse = { candidate: JobCandidate };

/** POST /api/v1/console/candidates/:jobCandidateId. Audited as MOVE_CANDIDATE. */
export type MoveCandidateRequest = {
  stage: CandidateStage;
  note?: string | null;
  reason: string;
  version: number;
};

export type MoveCandidateResponse = { candidate: JobCandidate };

// ── Learning ─────────────────────────────────────────────────────────────
// Open to everybody, because the thing you do here is your own training. What
// only HR sees is the company-wide half, and a course can be required *by* a
// permission — the same object model read from the other end, so the course is
// finished before the grant rather than after.

export type EnrolmentStatus = "enrolled" | "in_progress" | "done";

export type Course = {
  courseId: string;
  title: string;
  audience: string | null;
  minutes: number | null;
  /** The permission this course is required by, if any. */
  requiredForSettingId: string | null;
  requiredOfEverybody: boolean;
  active: boolean;
  /** How many people are enrolled, and how many have finished. */
  enrolled: number;
  done: number;
  version: number;
};

export type Enrolment = {
  courseEnrolmentId: string;
  courseId: string;
  title: string;
  status: EnrolmentStatus;
  progress: number;
  dueOn: string | null;
  completedAt: string | null;
  version: number;
};

/** A team, and how many of its people have finished what they must. */
export type TeamLearning = {
  staffTeamId: string;
  name: string;
  people: number;
  done: number;
};

/**
 * GET /api/v1/console/courses
 *
 * No feature. Everybody sees the catalogue and their own enrolments; the
 * company-wide half comes back only for somebody in HR. A plain read.
 */
export type ListCoursesResponse = {
  courses: readonly Course[];
  mine: readonly Enrolment[];
  /** Empty for anybody not in HR. */
  byTeam: readonly TeamLearning[];
};

/** POST /api/v1/console/courses — adds one. Audited as EDIT_COURSE. */
export type AddCourseRequest = {
  title: string;
  audience?: string | null;
  minutes?: number | null;
  requiredForSettingId?: string | null;
  requiredOfEverybody?: boolean;
  reason: string;
};

export type AddCourseResponse = { course: Course };

/**
 * POST /api/v1/console/courses/:courseId/enrol — your own enrolment, or moving
 * it along. Needs no feature: enrolling needs nobody's permission, it is the
 * grant behind the course that does. Audited as ENROL_IN_COURSE.
 */
export type EnrolRequest = {
  /** 0–100. 100 finishes it. */
  progress: number;
};

export type EnrolResponse = { enrolment: Enrolment };

// ── Actions, for one person ──────────────────────────────────────────────
// The console tab of Feature Enablements, over actions. An action is not a
// third kind of switch: what is switched on is the feature that unlocks it
// and, where the action needs one, the permission that gates it. This answers
// what somebody may actually do, and says which of those two decided it.

/** Where an answer came from, in the vocabulary Feature Enablements uses. */
export type ActionSource = "default" | "allStaff" | "staffRole" | "staffMember";

/** Why an action is not available, when it is not. */
export type ActionBlocker = "feature_off" | "permission_not_held";

export type StaffActionItem = {
  actionId: StaffActionId;
  area: string;
  sensitivity: "low" | "high" | "critical";
  /** Whether they can take it today, feature and permission together. */
  value: boolean;
  source: ActionSource;
  /** The roles granting it, when that is where the answer came from. */
  grantedBy: readonly string[];
  /** Which of the two said no. Null when they can take it. */
  blockedBy: ActionBlocker | null;
  /** The feature that unlocks it. Null for an action that needs none. */
  requiredFeature: string | null;
  /** The permission that gates it, when it has one (ADR-0019). */
  permission: { settingId: string; approvable: boolean; limited: boolean } | null;
  /** The most they may do alone, one per currency. Empty: no ceiling. */
  limits: readonly MoneyLimit[];
  /** True when they hold it but above their ceiling it becomes a request. */
  mustAsk: boolean;
  /** The reason recorded on the value set for them, when one is. */
  reason: string | null;
  expiresAt: string | null;
  /** Whether this caller could change this row for this subject. */
  editable: boolean;
  notEditable?: "own_account" | "critical_requires_owner" | "no_feature_to_switch";
};

/**
 * Who the table is about: one employee, or one role.
 *
 * A role has no resolved answer of its own the way a person does — it grants
 * or it does not — so `source` on a role row is only ever `staffRole` (this
 * role grants it) or `default` (it does not).
 */
export type ActionSubject =
  | { kind: "employee"; uuid: number; name: string; roles: readonly string[] }
  | { kind: "role"; staffRoleId: string; name: string; memberCount: number };

/**
 * GET /api/v1/console/staff-actions?uuid=62956136
 * GET /api/v1/console/staff-actions?staffRoleId=…
 *
 * Every action, for one employee or one role, and where each answer came
 * from. Needs the same features as the console features screen: this says
 * what somebody may do, which is the same thing read another way. A plain
 * read.
 */
export type ListStaffActionsResponse = {
  actions: readonly StaffActionItem[];
  subject: ActionSubject;
};

/**
 * POST /api/v1/console/action-enablements
 *
 * Turns actions on or off for one employee or one role, by writing the feature
 * that unlocks each one at that scope. One endpoint for both, because the two
 * differ only in which column the value lands in.
 *
 * Only the feature. The permission half is not a switch: granting "Settle a
 * refund" means granting it *up to* an amount, and a boolean cannot say that —
 * so an action still gated by a permission the subject does not hold keeps
 * saying so, and Permissions is where that is given.
 *
 * Audited as UPDATE_ACTION_ENABLEMENT, naming the subject it was about.
 */
export type ActionEnablementChange = {
  actionId: string;
  /** True switches it on, false off, null clears it back to what is inherited. */
  value: boolean | null;
  /** The version of the value already set here, when one is. */
  version?: number;
};

export type UpdateActionEnablementsRequest = {
  subject: { uuid: number } | { staffRoleId: string };
  /**
   * Every switch that moved, in one Save — the shape Feature Enablements
   * already uses. Toggling four things is four rows in one transaction and
   * one reason, not four dialogs: they either all land or none of them do,
   * and the person sees what they are about to do before it happens.
   */
  changes: readonly ActionEnablementChange[];
  reason: string;
};

/** The whole list again, so the screen never renders a value it guessed. */
export type UpdateActionEnablementsResponse = ListStaffActionsResponse;
